import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { auth, db } from '../../services/firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { translations } from '../../i18n';
import './register.css';

type Method = 'email' | 'google' | null;
type Step = 'method' | 'form' | 'verify-email' | 'choose-username';

const getPasswordStrength = (pw: string) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

const getPasswordErrors = (pw: string): string[] => {
  const errors: string[] = [];
  if (pw.length < 8) errors.push('At least 8 characters');
  if (!/[A-Z]/.test(pw)) errors.push('At least one uppercase letter');
  if (!/[0-9]/.test(pw)) errors.push('At least one number');
  if (!/[^A-Za-z0-9]/.test(pw)) errors.push('At least one symbol (!@#$%^&*)');
  return errors;
};

const strengthColor = ['', '#ef4444', '#f97316', '#eab308', '#22c55e'];

const checkUsername = async (username: string): Promise<boolean> => {
  if (username.length < 3) return false;
  const ref = doc(db, 'usernames', username.toLowerCase());
  const snap = await getDoc(ref);
  return !snap.exists();
};

const saveUsername = async (uid: string, username: string, withPassword = false) => {
  const lower = username.trim().toLowerCase();
  await setDoc(doc(db, 'usernames', lower), { uid, createdAt: serverTimestamp() });
  await setDoc(doc(db, 'users', uid), {
    username: lower,
    displayName: username.trim(),
    createdAt: serverTimestamp(),
    ...(withPassword && { hasPassword: true }),
  }, { merge: true });
};

export const Register = () => {
  const navigate = useNavigate();
  const lang = (localStorage.getItem('lang') as 'ES' | 'EN') || 'ES';
  const l = translations[lang].register;
  const strengthLabel = ['', l.strengthWeak, l.strengthFair, l.strengthGood, l.strengthStrong];

  const [step, setStep] = useState<Step>('method');
  const [method, setMethod] = useState<Method>(null);
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = getPasswordStrength(password);
  const usernameTimeout = useRef<any>(null);

  const handleUsernameChange = (val: string) => {
    setUsername(val);
    setUsernameAvailable(null);
    clearTimeout(usernameTimeout.current);
    if (val.length < 3) return;
    setCheckingUsername(true);
    usernameTimeout.current = setTimeout(async () => {
      const available = await checkUsername(val);
      setUsernameAvailable(available);
      setCheckingUsername(false);
    }, 600);
  };

  const handleGoogle = async () => {
    try {
      setLoading(true); setError('');
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user = result.user;
      // Check if user already has a complete profile
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const data = userDoc.data();
      if (data?.username && data?.hasPassword) {
        // Already complete — go to chat
        navigate('/chat');
        return;
      }
      setMethod('google');
      setStep('choose-username');
    }
    catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };

  const handleEmailRegister = async () => {
    setError('');
    if (!usernameAvailable) { setError(l.errorUsername); return; }
    if (password !== confirm) { setError(l.confirmNoMatch); return; }
    const pwErrors = getPasswordErrors(password);
    if (pwErrors.length > 0) { setError(pwErrors[0]); return; }
    try {
      setLoading(true);
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: username.trim() });
      await saveUsername(cred.user.uid, username.trim(), true);
      await sendEmailVerification(cred.user);
      setStep('verify-email');
    } catch (e: any) {
      if (e.code === 'auth/email-already-in-use') {
        setError('Este correo ya tiene una cuenta. Inicia sesión.');
      } else {
        setError(e.message);
      }
    } finally { setLoading(false); }
  };

  const handleSaveUsername = async () => {
    setError('');
    if (!usernameAvailable) { setError(l.errorUsername); return; }
    if (password.length < 6) { setError(l.errorPasswordShort); return; }
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) throw new Error(l.errorNoSession);
      await updateProfile(user, { displayName: username.trim() });
      await saveUsername(user.uid, username.trim(), true);
      navigate('/chat');
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="reg-page">
      <div className="reg-glow reg-glow--1" />
      <div className="reg-glow reg-glow--2" />
      <div className="reg-card">
        <div className="reg-logo" onClick={() => navigate('/')}>
          <span className="reg-logo__text" style={{ color: "var(--primary)" }}>TolkX</span>
        </div>

        {/* ── METHOD ── */}
        {step === 'method' && (
          <div className="reg-step">
            <h2 className="reg-title">{l.title}</h2>
            <p className="reg-sub">{l.sub}</p>
            <button className="reg-submit" onClick={() => { setMethod('email'); setStep('form'); }}>{l.emailBtn}</button>
            <div className="reg-divider"><span>O</span></div>
            <div className="reg-social-btns">
              <button className="reg-social-btn" onClick={handleGoogle} disabled={loading}>
                <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                {l.google}
              </button>
            </div>
            <p className="reg-switch">{l.hasAccount}{' '}<span className="reg-link" onClick={() => navigate('/login')}>{l.loginLink}</span></p>
          </div>
        )}

        {/* ── FORM EMAIL ── */}
        {step === 'form' && method === 'email' && (
          <div className="reg-step">
            <button className="reg-back" onClick={() => setStep('method')}>{l.back}</button>
            <h2 className="reg-title">{l.formTitle}</h2>
            <p className="reg-sub">{l.formSub}</p>
            {error && <div className="reg-error">{error}</div>}

            <div className="reg-field">
              <label className="reg-label">{l.username}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">@</span>
                <input className="reg-input" placeholder={l.usernamePlaceholder} value={username} onChange={e => handleUsernameChange(e.target.value)} />
                {checkingUsername && <span className="reg-input-status">⏳</span>}
                {!checkingUsername && usernameAvailable === true && <span className="reg-input-status reg-input-status--ok">✓</span>}
                {!checkingUsername && usernameAvailable === false && <span className="reg-input-status reg-input-status--err">✗</span>}
              </div>
              {usernameAvailable === true && <p className="reg-hint reg-hint--ok">{l.usernameAvailable}</p>}
              {usernameAvailable === false && <p className="reg-hint reg-hint--err">{l.usernameTaken}</p>}
            </div>

            <div className="reg-field">
              <label className="reg-label">{l.emailLabel}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">✉</span>
                <input className="reg-input" type="email" placeholder={l.emailPlaceholder} value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="reg-field">
              <label className="reg-label">{l.passwordLabel}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">🔒</span>
                <input className="reg-input" type={showPassword ? 'text' : 'password'} placeholder={l.passwordPlaceholder} value={password} onChange={e => setPassword(e.target.value)} />
                <button className="reg-toggle" type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} color="var(--text-dim)" /> : <Eye size={18} color="var(--text-dim)" />}
                </button>
              </div>
              {password && (
                <>
                  <div className="reg-strength">
                    <div className="reg-strength__bars">
                      {[1,2,3,4].map(i => <div key={i} className="reg-strength__bar" style={{ background: i <= strength ? strengthColor[strength] : 'var(--glass-border)' }} />)}
                    </div>
                    <span className="reg-strength__label" style={{ color: strengthColor[strength] }}>{strengthLabel[strength]}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                    {[
                      { label: '8+ chars', ok: password.length >= 8 },
                      { label: 'Uppercase', ok: /[A-Z]/.test(password) },
                      { label: 'Number', ok: /[0-9]/.test(password) },
                      { label: 'Symbol', ok: /[^A-Za-z0-9]/.test(password) },
                    ].map(({ label, ok }) => (
                      <span key={label} style={{
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                        borderRadius: '999px',
                        background: ok ? 'rgba(34,197,94,0.12)' : 'var(--input-bg)',
                        color: ok ? '#22c55e' : 'var(--text-dim)',
                        border: `1px solid ${ok ? '#22c55e44' : 'var(--glass-border)'}`,
                        transition: 'all 0.2s',
                      }}>
                        {ok ? '✓' : '○'} {label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="reg-field">
              <label className="reg-label">{l.confirmLabel}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">🔒</span>
                <input className="reg-input" type={showConfirm ? 'text' : 'password'} placeholder={l.confirmPlaceholder} value={confirm} onChange={e => setConfirm(e.target.value)} />
                <button className="reg-toggle" type="button" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff size={18} color="var(--text-dim)" /> : <Eye size={18} color="var(--text-dim)" />}
                </button>
              </div>
              {confirm && password !== confirm && <p className="reg-hint reg-hint--err">{l.confirmNoMatch}</p>}
              {confirm && password === confirm && <p className="reg-hint reg-hint--ok">{l.confirmMatch}</p>}
            </div>

            <button className="reg-submit" onClick={handleEmailRegister} disabled={loading}>
              {loading ? l.loading : l.submit}
            </button>
          </div>
        )}

        
        {/* ── OTP ── */}
        
        {/* ── VERIFY EMAIL ── */}
        {step === 'verify-email' && (
          <div className="reg-step reg-step--center">
            <div className="reg-check-icon">📧</div>
            <h2 className="reg-title">{l.verifyTitle}</h2>
            <p className="reg-sub">{l.verifySub} <strong>{email}</strong>.</p>
            <button className="reg-submit" onClick={() => navigate('/login')}>{l.verifyBtn}</button>
            <p className="reg-hint" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              {l.verifyNoArrived} <span className="reg-link" onClick={handleEmailRegister}>{l.verifyResend}</span>
            </p>
          </div>
        )}

        {/* ── CHOOSE USERNAME ── */}
        {step === 'choose-username' && (
          <div className="reg-step">
            <h2 className="reg-title">{l.chooseTitle}</h2>
            <p className="reg-sub">{l.chooseSub}</p>
            {error && <div className="reg-error">{error}</div>}

            <div className="reg-field">
              <label className="reg-label">{l.username}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">@</span>
                <input className="reg-input" placeholder={l.usernamePlaceholder} value={username} onChange={e => handleUsernameChange(e.target.value)} />
                {checkingUsername && <span className="reg-input-status">⏳</span>}
                {!checkingUsername && usernameAvailable === true && <span className="reg-input-status reg-input-status--ok">✓</span>}
                {!checkingUsername && usernameAvailable === false && <span className="reg-input-status reg-input-status--err">✗</span>}
              </div>
              {usernameAvailable === true && <p className="reg-hint reg-hint--ok">{l.usernameAvailable}</p>}
              {usernameAvailable === false && <p className="reg-hint reg-hint--err">{l.usernameTaken}</p>}
            </div>

            <div className="reg-field">
              <label className="reg-label">{l.backupPassword}</label>
              <div className="reg-input-wrap">
                <span className="reg-input-icon">🔒</span>
                <input className="reg-input" type={showPassword ? 'text' : 'password'} placeholder={l.backupPlaceholder} value={password} onChange={e => setPassword(e.target.value)} />
                <button className="reg-toggle" type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} color="var(--text-dim)" /> : <Eye size={18} color="var(--text-dim)" />}
                </button>
              </div>
            </div>

            <button className="reg-submit" onClick={handleSaveUsername} disabled={loading || !usernameAvailable}>
              {loading ? l.saving : l.saveBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};